import { apiRequest, ApiResponse } from '../request'

/** Ações que o backend define (`AI_USER_ACTIONS`). Os nomes têm de bater. */
export const AI_USER_ACTION = {
    sendMessage: 'send_message',
    askSupervisor: 'ask_supervisor',
    noReply: 'no_reply',
    moveDealStage: 'move_deal_stage',
    schedulingOpen: 'scheduling_open',
    schedulingSearchCatalog: 'scheduling_search_catalog',
    schedulingAddItem: 'scheduling_add_item',
    schedulingRemoveItem: 'scheduling_remove_item',
    schedulingChooseVariation: 'scheduling_choose_variation',
    schedulingImportOrder: 'scheduling_import_order',
    schedulingChoosePartner: 'scheduling_choose_partner',
    schedulingChooseProfessional: 'scheduling_choose_professional',
    schedulingApplyCoupon: 'scheduling_apply_coupon',
    schedulingChargeSeparately: 'scheduling_charge_separately',
    simulatePayment: 'simulate_payment',
    schedulingChooseAddress: 'scheduling_choose_address',
    schedulingChoosePayment: 'scheduling_choose_payment',
    schedulingPayLater: 'scheduling_pay_later',
    schedulingSubmit: 'scheduling_submit',
    schedulingPayOnBillingScreen: 'scheduling_pay_on_billing_screen',
    billingSetPayment: 'billing_set_payment',
    billingSplit: 'billing_split',
    billingSave: 'billing_save',
    appointmentSetSchedule: 'appointment_set_schedule',
    appointmentFindSlots: 'appointment_find_slots',
} as const

export type AiUserActionName = (typeof AI_USER_ACTION)[keyof typeof AI_USER_ACTION]

/** Motivos da `ask_supervisor` (backend `AI_USER_SUPERVISOR_REASON`). Os valores têm de bater. */
export const AI_USER_SUPERVISOR_REASON = {
    information: 'information',
    partnerSchedule: 'partner_schedule',
    cancellation: 'cancellation',
    withdrawal: 'withdrawal',
} as const

export type AiUserSupervisorReason = (typeof AI_USER_SUPERVISOR_REASON)[keyof typeof AI_USER_SUPERVISOR_REASON]

/** Motivos em que o contato recebe um aviso de espera antes da consulta ao supervisor. */
export const AI_USER_HOLDING_REASONS: readonly AiUserSupervisorReason[] = [
    AI_USER_SUPERVISOR_REASON.partnerSchedule,
    AI_USER_SUPERVISOR_REASON.cancellation,
    AI_USER_SUPERVISOR_REASON.withdrawal,
]

export interface AiUserTurnMessage {
    role: 'user' | 'assistant' | 'tool'
    content?: string | null
    tool_call_id?: string
    tool_calls?: unknown[]
}

export interface AiUserToolCall {
    id: string
    type: 'function'
    function: { name: string; arguments: string }
}

/** Créditos de IA esgotados: o Modo IA para e mostra o motivo. */
export const AI_CREDITS_EXHAUSTED_CODE = 'AI_CREDITS_EXHAUSTED'

export type AiUserTurnResult =
    | {
          success: true
          message: { content: string | null; toolCalls: AiUserToolCall[] }
          finishReason: string | null
      }
    | { success: false; code?: string; error: string }

/**
 * Um passo do usuário de IA. O prompt de sistema é montado no backend; daqui vai
 * só a conversa e o que a tela mostra. O consumo é debitado dos créditos de IA.
 */
/**
 * Pedido do passo em dados: o backend põe o texto (do painel MASTER) em volta e o manda
 * como primeira mensagem, antes de `messages`.
 */
export interface AiUserTurnStep {
    kind: 'decision' | 'handover' | 'waiting'
    contactName?: string | null
    transcript: string
    context?: string | null
    whatsappWindowClosed?: boolean
    pendingQuestion?: string | null
    holding?: boolean
}

export async function aiUserTurn(data: {
    step?: AiUserTurnStep
    messages: AiUserTurnMessage[]
    actions?: AiUserActionName[]
    chatId?: number
    /** Imagens que o contato mandou nesta conversa (o backend confere a origem). */
    images?: string[]
}): Promise<ApiResponse<AiUserTurnResult>> {
    return apiRequest<AiUserTurnResult>('ai-user/turn', { data }, 'post')
}

export interface AiUserPipelineStage {
    id: number
    name: string
    sortOrder: number
    isWonStage: boolean
    isLostStage: boolean
    /** Etapa que só uma pessoa ou um fluxo muda: a IA não leva o card para lá nem o tira de lá. */
    hiddenFromAi: boolean
}

export interface AiUserPipeline {
    id: number
    name: string
    description: string | null
    stages: AiUserPipelineStage[]
}

/** Funil próprio e carteiras do usuário de IA logado. */
export interface AiUserProfile {
    id: number
    name: string
    pipelines: AiUserPipeline[]
    wallets: Array<{ id: number; name: string }>
}

export async function getAiUserProfile(): Promise<ApiResponse<AiUserProfile>> {
    return apiRequest<AiUserProfile>('ai-user/profile', {})
}

/** Oportunidade aberta do contato da conversa, num funil do usuário de IA. */
export interface AiUserConversationDeal {
    id: number
    title: string
    pipelineId: number
    stageId: number
    pipeline: { name: string }
    stage: { name: string }
}

export async function getAiUserConversationDeals(
    chatId: number
): Promise<ApiResponse<AiUserConversationDeal[]>> {
    return apiRequest<AiUserConversationDeal[]>(`ai-user/chats/${chatId}/deals`, {})
}

/** Pergunta da IA ao supervisor, ainda sem resposta. A IA está calada nessa conversa. */
export interface AiUserPendingGuidance {
    id: number
    chatId: number
    question: string
    createdAt: string
    chat: { name: string } | null
}

/**
 * Evento de socket na sala do usuário de IA quando uma venda dele é paga (backend
 * `AI_USER_FOLLOW_UP_PAID_SOCKET_EVENT`). Os nomes têm de bater.
 */
export const AI_USER_FOLLOW_UP_PAID_SOCKET_EVENT = 'ai-user:follow-up-paid'

/** Conversa passou a ser do usuário de IA (backend `AI_USER_CHAT_HANDOVER_SOCKET_EVENT`). */
export const AI_USER_CHAT_HANDOVER_SOCKET_EVENT = 'ai-user:chat-handover'

/** Conversa recebida que espera o usuário de IA (ele não escreveu desde a transferência). */
export interface AiUserHandoverCandidate {
    chatId: number
    name: string | null
    channel: string
    lastInboundAt: string | null
}

export async function getAiUserHandoverCandidates(): Promise<ApiResponse<AiUserHandoverCandidate[]>> {
    return apiRequest<AiUserHandoverCandidate[]>('ai-user/handovers', {})
}

export type AiUserFollowUpKind = 'EXAM' | 'CONSULTATION' | 'PROCEDURE'

/** Venda do usuário de IA paga, à espera da guia. */
export interface AiUserPaidFollowUp {
    id: number
    chatId: number
    billingId: number
    appointments: Array<{ kind: AiUserFollowUpKind; id: number }>
    paidAt: string | null
}

/** Registra a venda enviada: quando o pagamento entrar, a IA manda a guia. */
export async function registerAiUserFollowUp(
    chatId: number,
    data: { billingId: number; appointments: Array<{ kind: AiUserFollowUpKind; id: number }> }
): Promise<ApiResponse<unknown>> {
    return apiRequest(`ai-user/chats/${chatId}/follow-ups`, { data }, 'post')
}

export async function getAiUserPaidFollowUps(): Promise<ApiResponse<AiUserPaidFollowUp[]>> {
    return apiRequest<AiUserPaidFollowUp[]>('ai-user/follow-ups/paid', {})
}

export async function completeAiUserFollowUp(id: number): Promise<ApiResponse<unknown>> {
    return apiRequest(`ai-user/follow-ups/${id}/done`, {}, 'patch')
}

export async function getAiUserPendingGuidance(): Promise<ApiResponse<AiUserPendingGuidance[]>> {
    return apiRequest<AiUserPendingGuidance[]>('ai-user/guidance/pending', {})
}

export async function askAiUserSupervisor(
    chatId: number,
    question: string
): Promise<ApiResponse<{ id: number; chatId: number; question: string; createdAt: string }>> {
    return apiRequest(`ai-user/chats/${chatId}/guidance`, { data: { question } }, 'post')
}

export async function answerAiUserGuidance(
    id: number,
    answer: string
): Promise<ApiResponse<{ id: number; chatId: number }>> {
    return apiRequest(`ai-user/guidance/${id}/answer`, { data: { answer } }, 'patch')
}

/**
 * Vagas da agenda do profissional (agenda própria, fase 3 parte 2). `bookable: false`
 * diz por que não dá para marcar sozinha: o caminho é o supervisor.
 */
export type AiUserAvailability =
    | {
          bookable: false
          reason: 'intermediation' | 'no_agenda' | 'no_working_hours'
          professional: { id: number; name: string }
      }
    | {
          bookable: true
          professional: { id: number; name: string }
          arrivalOrder: boolean
          slotDuration: number
          days: Array<{ date: string; weekday: string; free: string[]; appointments: number }>
      }

/** Janela máxima por consulta (backend `AI_USER_AVAILABILITY_MAX_DAYS`). */
export const AI_USER_AVAILABILITY_MAX_DAYS = 14

export async function getAiUserAvailability(
    professionalId: number,
    from: string,
    to: string,
): Promise<ApiResponse<AiUserAvailability>> {
    return apiRequest<AiUserAvailability>(`ai-user/professionals/${professionalId}/availability`, {
        params: { from, to },
    })
}
